import { DrawerTrigger } from '@jetshop/ui/Modal/Drawer/DrawerTrigger';
import ChannelContext from '@jetshop/core/components/ChannelContext';
import ProductConfigurationContext from '@jetshop/core/components/ProductConfigurationProvider/ProductConfigurationContext';
import { useDynamicPrice } from '@jetshop/core/hooks/useDynamicPrice';
import { useProductVariants } from '@jetshop/core/hooks/useProductVariants';
import { useVariantFromUrl } from '@jetshop/core/hooks/useProductVariants/useVariantFromUrl';
import t from '@jetshop/intl';
import { default as Breadcrumbs } from '@jetshop/ui/Breadcrumbs';
import getParentOrCanonicalRoute from '@jetshop/ui/Breadcrumbs/getParentsOrCanonicalRoute';
import { Above } from '@jetshop/ui/Breakpoints';
import { SelectDropdown } from '@jetshop/ui/Select';
import { TopListItem } from '@jetshop/ui/Select/SelectDropdown';
import { useStockStatus } from '@jetshop/ui/Stock/StockStatusContainer';
import { Form } from 'formik';
import get from 'lodash.get';
import React, { useContext, useState, useRef } from 'react';
import styled from 'react-emotion';
import Helmet from 'react-helmet-async';
import { ErrorSpan as ErrorWrap, Label } from '../Forms/Input';
import { Price } from '../Price';
import { Favourite } from '../ProductList/Favourite';
import Button from '../ui/Button';
import AddToCartForm from './AddToCart/AddToCartForm';
import ImageContainer from './Images/ImageContainer';
import ProductInfo, { ProductSection } from './ProductInfo';
import ProductInfoAccordion from './ProductInfoAccordion';
import ProductPageLoadingState from './ProductPageLoadingState';
import ProductReviewCard from './ProductReviewCard';
import { RelatedProducts } from './RelatedProducts';
import StockStatusIndicator from './StockStatus/StockStatusIndicator';
import { Container } from './styledComponents';
import { VariantSelector } from './VariantSelector';
import UspBar from '../Layout/Header/UspBar';
import ProductOptions from './ProductOptions';
import ProductRow from '../Shortcodes/ProductRow';
import Sizeguide from './Sizeguide';
import SimilarProductsFallback from './SimilarProductsFallback';
import { ProductPackage } from './ProductPackage';
import { Below } from '@jetshop/ui/Breakpoints';
import HelloRetailRecommendations from '../../components/HelloRetail';
import { LipscoreProductSmallRating } from '../Lipscore/LipscoreProductSmallRating';
import { Upsell } from './Upsell/Upsell';

const ProductLeft = styled('div')`
  width: calc(100% - 560px);
  height: 100%;
  border-bottom: 1px solid #f3f3f3;
  ${({ theme }) => theme.below.md} {
    border: 0;
  }

  ${({ theme }) => theme.below.md} {
    width: 100%;
    height: auto;
  }

  .breadcrumbs {
    margin: 0;
    padding: 1rem 2rem;
    border-bottom: 1px solid #f3f3f3;
    ${({ theme }) => theme.below.md} {
      padding: 5px 20px;
    }
  }

  section {
    height: calc(100% - 51px);
    padding: 0;
    max-height: 900px;
  }
`;
const ProductRight = styled('div')`
  background: #f3f3f3;
  width: 560px;
  height: 100%;
  padding: 50px 40px;

  ${({ theme }) => theme.below.md} {
    padding: 10px 20px 20px 20px;
    width: 100%;
    height: auto;
  }

  .product-section {
    padding: 0;
    height: 100%;
    .product-details {
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
    }
  }
`;
const ProductUpper = styled('div')`
  min-height: calc(100vh - 197px);
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  ${({ theme }) => theme.below.md} {
    display: block;
  }
`;
const ProductLower = styled('div')`
  width: 100%;
  display: block;

  .video-wrapper {
    position: relative;
    padding-bottom: 56.25%; /* 16:9 */
    height: 0;
    margin: 2rem auto;
  }
  .video-wrapper iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;
const Upper = styled('div')``;
const Lower = styled('div')``;
export const VariantWrapper = styled('div')`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  > div,
  a {
    width: 100%;
  }
  a {
    text-align: right;
    text-decoration: underline;
    font-size: 12px;
    color: black;
    &:hover {
      color: ${({ theme }) => theme.colors.primary};
    }
  }
`;

const PickerWrapper = styled('div')`
  width: 100%;
  position: relative;
  ${TopListItem} {
    border-radius: 4px;
  }
`;

const DefaultWrapper = styled(PickerWrapper)`
  button {
    height: 48px !important;
  }
`;

const ErrorSpan = styled(ErrorWrap)`
  bottom: 0;
`;

const DefaultPicker = ({ option, error, ...props }) => (
  <DefaultWrapper>
    <Label for={`${option.name}-select-dropdown`}>{option.name}</Label>
    <SelectDropdown
      {...props}
      id={`${option.name}-select-dropdown`}
      data-testid={`${option.name}-select-dropdown`}
    />
    {error && <ErrorSpan>{error}</ErrorSpan>}
  </DefaultWrapper>
);

const StyledForm = styled(Form)`
  display: flex;
  width: 100%;
  flex-direction: column;
  margin-top: 10px;
  ${({ theme }) => theme.below.md} {
    align-items: center;
  }

  button {
    padding: 0;
    height: 36px;
    line-height: 36px;
  }
`;

const ProductContainer = styled('div')`
  display: flex;
  align-items: center;
  flex-direction: column;
  .packageRef {
    max-width: 100%;
  }
  header {
    .favorite-icon {
      text-align: right;
      button {
        background: none;
        border: 0;
        appearance: none;

        svg {
          height: 30px;
          width: 30px;
        }
      }
    }
    h1 {
      font-size: 25px;
      line-height: 1;
      font-weight: bold;
    }

    margin-bottom: 0;
  }
  .customer-comment-input-wrapper {
    width: 100%;
    .customer-comment-input {
      height: 44px;
    }
    .status-wrapper {
      top: -39px;
    }
  }

  .product-details {
    .new-price,
    .old-price {
      font-weight: 600;
      display: inline-block;
    }
    .new-price {
      font-size: 1.5rem;
    }
    .new-price {
      margin-right: 8px;
      color: ${({ theme }) => theme.colors.red};
    }
    .old-price {
      color: ${({ theme }) => theme.colors.mediumgrey};
    }
    .price {
      color: ${({ theme }) => theme.colors.primary};
      display: inline-block;
      font-size: 25px;
      line-height: 30px;
      font-weight: bold;
      font-family: ${({ theme }) => theme.fonts.secondary};
    }

    > p {
      font-size: 12px;
      line-height: 300%;
    }
  }
`;

const PackageButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.black};
  margin-top: 10px;
  font-size: 16px;
  margin-bottom: 1rem;
`;

const LogoWrapper = styled('div')`
  position: absolute;
  top: 0px;
  right: 0px;

  img {
    width: 70px;
  }
`;

const UspWrapper = styled('div')`
  position: relative;
  margin-top: 10px;
  ul {
    flex-direction: column;
    background: none !important;
    ${({ theme }) => theme.below.md} {
      padding: 8px 0;
    }
  }

  &.mtbstore {
    ul li {
      &:last-child {
        display: none !important;
      }
    }
  }

  ul li {
    margin-bottom: 8px;
    display: flex !important;
    font-weight: 500;
    align-items: center;
    span {
      font-size: 13px;
      margin-left: 5px;
      margin-top: -2px;
      display: block;
    }
  }
`;

const ShortProductInfoWrapper = styled('div')`
  margin-top: 30px;
  margin-bottom: 15px;

  p {
    margin: 0;
    font-size: 15px;
  }

  u {
    font-weight: bold;
    cursor: pointer;
    font-size: 15px;
    line-height: 18px;
  }
`;

const RecommendedWrapper = styled('div')`
  width: 100%;
  max-width: 1200px;

  margin: 0 auto;

  > div {
    .hello-retail-recommendations {
      .product-grid {
        margin: 0;
      }
    }
  }
`;

const VariantSizeguideWrapper = styled('div')`
  display: flex;
  justify-content: space-between;

  .title-stockstatus {
    display: flex;
    flex-direction: column;

    p {
      margin-top: 10px;
      font-size: 14px;
      line-height: 100%;
    }
  }

  .modalLink {
    width: auto;
    display: flex;
  }

  h3 {
    color: #000;
    font-family: Roboto;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    text-transform: unset;
  }
`;

function getBrand(product) {
  const field = product.customFields.find(({ key }) => key === 'brand');
  if (field) {
    return field.stringValue;
  }
}

const ProductMeta = ({ product, selectedVariation }) => {
  const { selectedChannel } = useContext(ChannelContext);

  const prod = selectedVariation || product;
  const brand = getBrand(product);

  return (
    <Helmet>
      <meta property="og:title" content={product.name} />
      <meta
        property="product:original_price:amount"
        content={prod.price.incVat}
      />
      <meta
        property="product:price:amount"
        content={prod.previousPrice.incVat}
      />
      <meta
        property="og:price:currency"
        content={selectedChannel.currency.id}
      />
      {product.images.length > 0 && (
        <meta property="og:image" content={product.images[0].url} />
      )}
      <meta
        property="product:availability"
        content={prod.stockStatus.buyable ? 'instock' : 'oos'}
      />
      {brand && <meta property="product:brand" content={brand} />}
      <meta
        property="product:category"
        content={product.canonicalCategory && product.canonicalCategory.name}
      />
      <meta
        property="og:url"
        content={`${selectedChannel.url}${product.primaryRoute.path}`}
      />
    </Helmet>
  );
};

const Product = ({ result: { data, loading }, product, cacheHit }) => {
  const { selectedChannel } = useContext(ChannelContext);
  // const variantHandler = useProductVariants(
  //   product.articleNumber ? product : undefined
  // );

  /**
   * The isEmpty function checks if an object is empty.
   * @param obj Pass in an object to the function
   * @return True if the object has no properties, and false if the object has at least one property
   * @docauthor Anders Zetterström @ R3
   */
  function isEmpty(obj) {
    return Object.keys(obj).length === 0;
  }

  const initialVariant = useVariantFromUrl();
  const inititalVariantStockOK = product?.variants?.values?.find((v) => {
    return v?.stockStatus?.buyable;
  });

  const variantHandler = useProductVariants(product, {
    initialVariant: isEmpty(initialVariant)
      ? inititalVariantStockOK
      : initialVariant,
    preselectCheapest: true,
  });

  const {
    selectedVariant: selectedVariation,
    validation: variantValidation,
    getMissingOptions,
  } = variantHandler;

  const packagePlacement = useRef(null);

  const configurationContext = useContext(ProductConfigurationContext);
  const stockStatus = useStockStatus(selectedVariation || product);
  const [activeTab, setActiveTab] = useState(false);
  const price = useDynamicPrice(product, selectedVariation);
  const images = get(product, 'images', []);

  // id 1 = skistore
  //Product page - Products purchased together - Box 1 (ski)
  const helloRetailRecommendationsId1 =
    selectedChannel.id === 1
      ? 'k5fbbc43ba5a4ee1229620b3a'
      : 'k5fbbc209a5a4ee122960c396';

  const helloRetailRecommendationsId2 =
    selectedChannel.id === 1
      ? 'k5fbbc43ba5a4ee1229620b03'
      : 'k5fbbc252a5a4ee122960ed15';

  const helloRetailRecommendationsId3 =
    selectedChannel.id === 1
      ? 'k5fbbc43aa5a4ee1229620af2'
      : 'k5fbbc292a5a4ee1229611590';

  // Return early if there is no cache hit
  if (!cacheHit)
    return (
      <ProductContainer>
        <ProductPageLoadingState />
      </ProductContainer>
    );

  const parents = getParentOrCanonicalRoute(
    get(data, 'route.parents'),
    product
  );

  const relatedProducts = get(product, 'relatedProducts');

  const youtube = product.customFields.find(({ key }) => key === 'youtube');
  const citat = product.customFields.find(({ key }) => key === 'citat');
  const liknandeProdukter = product.customFields.find(
    ({ key }) => key === 'liknandeProdukter'
  );
  const specifikationer = product.customFields.find(
    ({ key }) => key === 'specifikationer'
  );
  const sizeguideId = product.customFields.find(
    ({ key }) => key === 'storleksGuide'
  );

  function calculateBuyButtonText(product, variantValidation) {
    if (product.stockStatus.buyable && variantValidation === 'valid') {
      return t('Add to cart');
    }
    if (variantValidation === 'incomplete' && product.stockStatus.buyable) {
      return t('Välj alternativ');
    }
    return t('Ej i lager');
  }

  const handleClick = (index) => {
    setActiveTab(index);
    document
      .getElementById('product-tabs')
      .scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  const hasPackage =
    product.customFields.filter((field) => {
      return field.key.indexOf('rekommenderadeBindningar') > -1;
    }).length > 0;

  const goToPackageMobile = () => {
    packagePlacement.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const goToPackage = () => {
    const topScroll =
      packagePlacement.current.getBoundingClientRect().top +
      document.documentElement.scrollTop -
      150;
    window.scrollTo({ top: topScroll, behavior: 'smooth' });
  };

  let title = false;

  if (!product?.variants?.options?.[0]?.name) {
    title = false;
  }

  if (
    product?.variants?.options?.[0]?.name?.toLowerCase()?.indexOf('storlek') >
    -1
  ) {
    title = 'Storlek';
  } else if (
    product?.variants?.options?.[0]?.name?.toLowerCase()?.indexOf('längd') > -1
  ) {
    title = 'Längd';
  }

  return (
    <ProductContainer>
      {product?.canonicalCategory ? (
        <Helmet
          title={
            selectedChannel?.id === 1
              ? product?.name +
                ' | ' +
                product?.canonicalCategory?.name +
                ' - SkiStore'
              : product?.name +
                ' | ' +
                product?.canonicalCategory?.name +
                ' - MTBStore'
          }
        />
      ) : null}

      <Container>
        <ProductUpper>
          <ProductLeft>
            <Breadcrumbs
              className="breadcrumbs"
              parents={parents}
              breadcrumbText={product.breadcrumbText}
            />
            <ProductMeta {...{ product, selectedVariation }} />
            <ProductSection>
              {images && (
                <ImageContainer
                  images={images}
                  variant={selectedVariation}
                  badges={product.badges}
                  youtube={youtube ? youtube : null}
                />
              )}
            </ProductSection>
          </ProductLeft>
          <ProductRight>
            <ProductSection className="product-section">
              <div className="product-details">
                <Upper>
                  <header>
                    <Favourite
                      className="favorite-icon"
                      product={product}
                      variant={selectedVariation}
                      style={{
                        marginLeft: 'auto',
                        fontSize: '1.5em',
                      }}
                    />
                    <h1 data-testid="product-title">
                      {product.name || 'Loading...'}
                    </h1>
                  </header>
                  {product && (
                    <LipscoreProductSmallRating
                      product={product}
                      handleClick={handleClick}
                    />
                  )}
                  <Price
                    {...price}
                    css={{
                      marginBottom: '0px',
                      opacity:
                        configurationContext && configurationContext.loading
                          ? 0.3
                          : 1,
                    }}
                  />
                  {product && (
                    <ProductOptions
                      mainProductId={product.id}
                      productColor={product.subName}
                      productArtNo={product.articleNumber}
                      mainProduct={product}
                    />
                  )}
                  <VariantSizeguideWrapper>
                    <div className="title-stockstatus">
                      {title && (
                        <h3>
                          {t.rich('Select {option}', {
                            option: title ? title : option?.name,
                          })}
                        </h3>
                      )}
                      {selectedVariation && (
                        <StockStatusIndicator
                          status={stockStatus.status}
                          text={stockStatus.text}
                          variants={product.variants}
                          xr
                          selectedVariation={selectedVariation}
                        />
                      )}
                    </div>

                    {sizeguideId && (
                      <Sizeguide
                        product={product}
                        sizeguideId={sizeguideId.stringValue}
                      >
                        {t('Size guide')}
                      </Sizeguide>
                    )}
                  </VariantSizeguideWrapper>
                  <VariantWrapper>
                    <VariantSelector
                      product={product}
                      variantHandler={variantHandler}
                      dropDownSelect={false}
                    />
                  </VariantWrapper>
                  <AddToCartForm
                    product={product}
                    variant={selectedVariation}
                    getMissingOptions={getMissingOptions}
                  >
                    {hasPackage ? (
                      <div className="packageRef" ref={packagePlacement}>
                        <ProductPackage
                          product={product}
                          goToPackage={goToPackage}
                          variant={selectedVariation ? selectedVariation : null}
                          variantValidation={variantValidation}
                        />
                      </div>
                    ) : (
                      <StyledForm>
                        <DrawerTrigger id="cart-drawer">
                          {(drawer) => (
                            <Button
                              data-testid="add-to-cart"
                              type="submit"
                              disabled={
                                !(
                                  product.stockStatus.buyable &&
                                  variantValidation === 'valid'
                                )
                              }
                              onClick={
                                drawer.isOpen
                                  ? drawer.hideTarget
                                  : drawer.showTarget
                              }
                            >
                              {product?.stockStatus?.text?.includes(
                                'Finns i butiken'
                              )
                                ? 'Säljs endast i butik'
                                : calculateBuyButtonText(
                                    product,
                                    variantValidation
                                  )}
                            </Button>
                          )}
                        </DrawerTrigger>
                      </StyledForm>
                    )}
                  </AddToCartForm>

                  {!product?.hasVariants && (
                    <StockStatusIndicator
                      status={stockStatus.status}
                      text={stockStatus.text}
                      variants={product.variants}
                      selectedVariation={selectedVariation}
                    />
                  )}

                  <UspWrapper
                    className={
                      selectedChannel.id === 1 ? 'skistore' : 'mtbstore'
                    }
                  >
                    <UspBar productPage={true} />
                    <LogoWrapper>
                      <img src="https://x.klarnacdn.net/payment-method/assets/badges/generic/black/klarna.svg" />
                    </LogoWrapper>
                  </UspWrapper>
                  {product?.hasUpsell && <Upsell product={product} />}
                </Upper>
                <Lower>
                  {citat && <ProductReviewCard productReview={citat} />}
                </Lower>
              </div>
            </ProductSection>
          </ProductRight>
        </ProductUpper>

        <ProductLower>
          <div id="product-tabs">
            <Above breakpoint="md">
              {(matches) =>
                matches ? (
                  <ProductInfo
                    product={product}
                    specs={specifikationer}
                    setActiveTab={setActiveTab}
                    activeIndex={activeTab ? activeTab : '1'}
                  />
                ) : (
                  <ProductInfoAccordion
                    product={product}
                    specs={specifikationer}
                    setActiveTab={setActiveTab}
                    activeIndex={activeTab ? activeTab : false}
                  />
                )
              }
            </Above>
          </div>
        </ProductLower>
        {youtube ? (
          <Below breakpoint="md">
            <ProductLower>
              <div className="video-wrapper">
                <iframe
                  title={product.name}
                  width="100%"
                  height="auto"
                  src={
                    'https://www.youtube.com/embed/' +
                    youtube.stringValue.split('?v=')[1] +
                    '?mode=opaque&rel=0&autohide=1&showinfo=0&wmode=transparent&modestbranding=0&showinfo=0&rel=0'
                  }
                  frameBorder="0"
                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </ProductLower>
          </Below>
        ) : null}
      </Container>
      <ProductLower>
        <RecommendedWrapper>
          <HelloRetailRecommendations
            recommendationsList={[
              {
                title: 'Similar products',
                recommendationId: helloRetailRecommendationsId1,
                parents: parents,
              },
              {
                title: 'Others also bought',
                recommendationId: helloRetailRecommendationsId2,
                parents: parents,
              },
              {
                title: 'Recently viewed',
                recommendationId: helloRetailRecommendationsId3,
                parents: parents,
              },
            ]}
          />
        </RecommendedWrapper>
      </ProductLower>
    </ProductContainer>
  );
};

export default Product;
